import { FILTER_KIND_OUTPUT } from "../types/constants";
import { dateRangesEqual, isDateRangeSet } from "../utils/dateUtils";
import { filterTargetColumn } from "../utils/rangeGroupingUtils";
import { useReportStateContext } from "./useReportState";
import { useCallback, useMemo, useState } from "react";

const setsEqual = (a, b) => {
  if (!a) return b.size === 0;
  if (a.size !== b.size) return false;
  for (const value of b) {
    if (!a.has(value)) return false;
  }
  return true;
};

const initialPanelState = {
  column: "",
  kind: FILTER_KIND_OUTPUT,
  valueSearch: "",
  selectedValues: new Set(),
  dateRange: { from: null, to: null },
  numberRange: { min: null, max: null },
};

export const useAddFilterPanel = ({
  getColumnFilterType,
  isBinaryColumn,
  rangeGroupedColumns,
}) => {
  const {
    filterMaps,
    inputFilterMaps,
    setValueFilter,
    setBooleanFilter,
    setDateFilter,
    setNumberFilter,
    clearFilter,
  } = useReportStateContext();

  const [panelState, setPanelState] = useState(initialPanelState);

  const updateField = useCallback((field, value) => {
    setPanelState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setSelectedValues = useCallback((action) => {
    setPanelState((prev) => ({
      ...prev,
      selectedValues:
        typeof action === "function" ? action(prev.selectedValues) : action,
    }));
  }, []);
  const setDateRange = useCallback((action) => {
    setPanelState((prev) => ({
      ...prev,
      dateRange: typeof action === "function" ? action(prev.dateRange) : action,
    }));
  }, []);
  const setNumberRange = useCallback((action) => {
    setPanelState((prev) => ({
      ...prev,
      numberRange:
        typeof action === "function" ? action(prev.numberRange) : action,
    }));
  }, []);

  const setColumn = useCallback((column, kind = FILTER_KIND_OUTPUT) => {
    setPanelState((prev) => ({ ...prev, column, kind }));
  }, []);

  const { column, kind, selectedValues, dateRange, numberRange } = panelState;
  const targetColumn = filterTargetColumn(column, kind, rangeGroupedColumns);

  const {
    value: columnFilters,
    date: dateColumnFilters,
    number: numberColumnFilters,
  } = (kind === FILTER_KIND_OUTPUT ? filterMaps : inputFilterMaps) ?? {};

  const selectedAddFilterColumnType = getColumnFilterType(column, kind);
  const hasAddFilterPendingChanges = useMemo(() => {
    if (!column) return false;
    if (selectedAddFilterColumnType === "value") {
      const existingValues = columnFilters?.[targetColumn];
      return !setsEqual(existingValues, selectedValues);
    }
    if (selectedAddFilterColumnType === "date") {
      const existingRange = dateColumnFilters?.[targetColumn];
      const existingBlank = columnFilters?.[targetColumn]?.has("") === true;
      return (
        !dateRangesEqual(dateRange, existingRange ?? {}) ||
        (dateRange.isEmpty === true) !== existingBlank
      );
    }
    if (selectedAddFilterColumnType === "number") {
      const existingRange = numberColumnFilters?.[targetColumn];
      const existingBlank = columnFilters?.[targetColumn]?.has("") === true;
      return (
        numberRange.min !== (existingRange?.min ?? null) ||
        numberRange.max !== (existingRange?.max ?? null) ||
        (numberRange.isEmpty === true) !== existingBlank
      );
    }
    return false;
  }, [
    column,
    targetColumn,
    selectedAddFilterColumnType,
    selectedValues,
    dateRange,
    numberRange,
    columnFilters,
    dateColumnFilters,
    numberColumnFilters,
  ]);

  const resetPanel = useCallback(() => {
    setPanelState({ ...initialPanelState, selectedValues: new Set() });
  }, []);

  const applyFromPanel = useCallback(() => {
    if (!column || !selectedAddFilterColumnType) return;
    if (selectedAddFilterColumnType === "value") {
      if (selectedValues.size > 0) {
        if (isBinaryColumn(column, kind)) {
          const value = selectedValues.values().next().value;
          setBooleanFilter(targetColumn, value, kind);
        } else {
          setValueFilter(targetColumn, new Set(selectedValues), kind);
        }
      } else {
        clearFilter(targetColumn, kind);
      }
    } else if (selectedAddFilterColumnType === "date") {
      if (dateRange.isEmpty) {
        setValueFilter(targetColumn, new Set([""]), kind);
      } else if (isDateRangeSet(dateRange)) {
        setDateFilter(
          targetColumn,
          {
            from: dateRange.from,
            to: dateRange.to,
            ...(dateRange.dynamic ? { dynamic: dateRange.dynamic } : {}),
          },
          kind
        );
      } else {
        clearFilter(targetColumn, kind);
      }
    } else if (selectedAddFilterColumnType === "number") {
      if (numberRange.isEmpty) {
        setValueFilter(targetColumn, new Set([""]), kind);
      } else if (numberRange.min !== null || numberRange.max !== null) {
        setNumberFilter(
          targetColumn,
          { min: numberRange.min, max: numberRange.max },
          kind
        );
      } else {
        clearFilter(targetColumn, kind);
      }
    }
    resetPanel();
  }, [
    column,
    kind,
    targetColumn,
    selectedAddFilterColumnType,
    selectedValues,
    dateRange,
    numberRange,
    isBinaryColumn,
    setValueFilter,
    setBooleanFilter,
    setDateFilter,
    setNumberFilter,
    clearFilter,
    resetPanel,
  ]);

  const addFilterPanelSetters = useMemo(
    () => ({
      setColumn,
      setValueSearch: (value) => updateField("valueSearch", value),
      setSelectedValues,
      setDateRange,
      setNumberRange,
    }),
    [setColumn, updateField, setSelectedValues, setDateRange, setNumberRange]
  );

  return {
    addFilterPanel: panelState,
    targetColumn,
    addFilterPanelSetters,
    hasAddFilterPendingChanges,
    applyFromPanel,
    resetPanel,
  };
};

export default useAddFilterPanel;
